"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const steps = [
  {
    number: "01",
    icon: "UserPlusIcon",
    title: "Create your pet\'s profile",
    description:
      "Add your pet's name, breed, age, weight, vaccine history, allergies, and medical notes. It takes under 3 minutes and lives securely in the cloud.",
    color: "#5EBFB3",
    bg: "#E8F7F6",
    highlight: "Free, takes 3 minutes",
  },
  {
    number: "02",
    icon: "LinkIcon",
    title: "Connect with professionals",
    description:
      "Add your vet, groomer, trainer, and other professionals. They see only what you choose to share — keeping your pet's care coordinated in one place.",
    color: "#7C3AED",
    bg: "#EDE9FE",
    highlight: "You control who sees what",
  },
  {
    number: "03",
    icon: "ShieldCheckIcon",
    title: "Stay organized and safe",
    description:
      "Get smart reminders for vaccines and vet visits. Keep your pet's profile up to date. Share emergency info with a single tap if your pet ever gets lost.",
    color: "#0369A1",
    bg: "#E0F2FE",
    highlight: "Real-time alerts",
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white dark:bg-[#0f172a]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16 scroll-fade-up">
          <span className="teal-badge mb-4">How It Works</span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-[#1A2B2B] dark:text-slate-100 mt-4 mb-5 tracking-tight">
            Up and running in minutes
          </h2>
          <p className="text-lg text-[#4A6363] dark:text-slate-400 max-w-xl mx-auto leading-relaxed">
            No complicated setup. No tech expertise needed. DogFob is built for pet lovers, not engineers.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-10 relative">
          {/* Connector line (desktop) */}
          <div
            className="hidden md:block absolute top-[52px] left-[calc(16.67%+24px)] right-[calc(16.67%+24px)] h-[2px] z-0"
            style={{ background: "linear-gradient(to right, #5EBFB3, #7C3AED, #0369A1)" }}
          />

          {steps.map((step, idx) => (
            <div key={step.number} className={`scroll-fade-up stagger-${idx + 1} relative z-10`}>
              <div className="feature-card p-7 h-full flex flex-col transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl">
                {/* Icon circle */}
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 relative animate-pulse-glow"
                    style={{ background: step.bg }}
                  >
                    <Icon name={step.icon as "UserPlusIcon"} size={24} style={{ color: step.color }} />
                    <span
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full text-white text-[10px] font-extrabold font-heading flex items-center justify-center"
                      style={{ background: step.color }}
                    >
                      {idx + 1}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest" style={{ color: step.color }}>
                      Step {step.number}
                    </p>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-[#1A2B2B] dark:text-slate-100 mb-3 font-heading leading-snug">
                  {step.title}
                </h3>
                <p className="text-[#4A6363] dark:text-slate-400 leading-relaxed text-sm flex-1">{step.description}</p>

                <div
                  className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full"
                  style={{ background: step.bg, color: step.color }}
                >
                  <Icon name="SparklesIcon" size={12} />
                  {step.highlight}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}